import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Compass, LockKeyhole, SearchX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { loginHref, logout } from "@/lib/api/auth";
import { useTenantPathFn } from "@/lib/tenant";

/**
 * The two dead ends, each saying what actually happened and offering a way out that
 * works.
 *
 * Neither page guesses. The 403 does not claim which role you have — that is the
 * server's answer and this page did not ask for it — it says which surfaces are
 * admin-only and offers to sign in as someone else.
 */

function Shell({
  icon,
  code,
  title,
  children,
  testid,
}: {
  icon: ReactNode;
  code: string;
  title: string;
  children: ReactNode;
  testid: string;
}) {
  return (
    <main className="flex min-h-[70vh] items-center justify-center p-6">
      <Card className="w-full max-w-lg" data-testid={testid}>
        <CardHeader className="space-y-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand">
            {icon}
          </span>
          <p className="font-mono text-xs uppercase tracking-wide text-ink-3">{code}</p>
          <CardTitle className="text-xl">{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </main>
  );
}

export function Forbidden403() {
  const resolve = useTenantPathFn();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  async function signOutAndBack() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      // The session may already be gone. Sending the browser to the login entry point is
      // correct either way, so the failure is not surfaced as an error the operator must act on.
    }
    window.location.assign(loginHref("/overview"));
  }

  return (
    <Shell
      testid="forbidden-403"
      code="403 — Forbidden"
      title="Your role does not include this page"
      icon={<LockKeyhole className="h-5 w-5" aria-hidden="true" />}
    >
      <CardDescription>
        The server refused this request because the page needs a higher role than your account
        holds. Roles are per operator and are set by an administrator on Administration →
        Operators.
      </CardDescription>
      <ul className="space-y-1 text-sm text-ink-2">
        <li>
          <span className="font-medium text-ink">reader</span> — reads every part of the console,
          changes nothing.
        </li>
        <li>
          <span className="font-medium text-ink">admin</span> — additionally starts scans,
          accepts findings, and manages the tenant credentials, settings, operators and the
          audit log.
        </li>
      </ul>
      <p className="font-mono text-xs text-ink-3">
        Refused path: {location.pathname}
        {location.search}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button asChild className="focus-ring">
          <Link to={resolve("/overview")} data-testid="forbidden-overview">
            Back to Overview
          </Link>
        </Button>
        <Button
          variant="outline"
          className="focus-ring"
          data-testid="forbidden-switch-account"
          disabled={signingOut}
          onClick={() => void signOutAndBack()}
        >
          {signingOut ? "Signing out…" : "Sign in as a different account"}
        </Button>
      </div>
    </Shell>
  );
}

export function NotFound404() {
  const resolve = useTenantPathFn();
  const location = useLocation();
  return (
    <Shell
      testid="not-found-404"
      code="404 — Not found"
      title="There is no page at this address"
      icon={<SearchX className="h-5 w-5" aria-hidden="true" />}
    >
      <CardDescription>
        Nothing in this console is served from{" "}
        <span className="font-mono">{location.pathname}</span>. If you followed a link from a
        report or a bookmark, the object it named may have been deleted — deleted objects stay in
        the inventory and are shown with the run that last saw them.
      </CardDescription>
      <div className="flex flex-wrap gap-2">
        <Button asChild className="focus-ring">
          <Link to={resolve("/overview")} data-testid="notfound-overview">
            <Compass className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Overview
          </Link>
        </Button>
        <Button asChild variant="outline" className="focus-ring">
          <Link to={resolve("/inventory")} data-testid="notfound-inventory">
            Browse the inventory
          </Link>
        </Button>
        <Button asChild variant="outline" className="focus-ring">
          <Link to={resolve("/posture")} data-testid="notfound-posture">
            Browse posture findings
          </Link>
        </Button>
      </div>
    </Shell>
  );
}
